<template>
  <section
    class="widget"
    :class="[
      `widget--${etat}`,
      `widget--${variante}`,
      { 'widget--reglable': reglable },
    ]"
    :style="teinteStyle"
  >
    <header class="widget__entete">
      <span
        v-if="icone && variante !== 'bandeau'"
        class="widget__icone"
        aria-hidden="true"
      >
        <Icon
          :icon="icone"
          :width="16"
        />
      </span>

      <h3 class="widget__titre">
        {{ titre }}
      </h3>

      <span
        v-if="etat !== 'repos'"
        class="widget__etat"
        :title="etat === 'attention' ? 'Quelque chose attend' : 'Rien à signaler'"
      />

      <slot name="indice" />

      <div class="widget__actions">
        <button
          v-if="lien"
          class="widget__lien"
          type="button"
          @click="$router.push(lien)"
        >
          {{ libelleLien }}
        </button>

        <!-- Les commandes de réglage ne s'affichent qu'au survol du widget :
             une croix et une poignée sur chaque carte, en permanence, c'est dix
             boutons qui réclament l'attention avant les chiffres. -->
        <span
          v-if="reglable"
          class="widget__poignee"
          title="Déplacer"
          aria-hidden="true"
        >
          <Icon
            icon="mdi:drag-horizontal-variant"
            :width="15"
          />
        </span>

        <el-popconfirm
          v-if="reglable"
          title="Retirer ce widget de votre accueil ?"
          confirm-button-text="Retirer"
          cancel-button-text="Annuler"
          @confirm="$emit('retirer')"
        >
          <template #reference>
            <button
              class="widget__retirer"
              type="button"
              title="Retirer de l'accueil"
              :aria-label="`Retirer ${titre} de l'accueil`"
            >
              <Icon
                icon="mdi:close"
                :width="14"
              />
            </button>
          </template>
        </el-popconfirm>
      </div>
    </header>

    <div
      v-loading="chargement"
      class="widget__corps"
    >
      <p
        v-if="!chargement && panne"
        class="widget__panne"
      >
        Indisponible pour le moment.
        <button
          class="widget__relancer"
          type="button"
          @click="$emit('recharger')"
        >
          Réessayer
        </button>
      </p>

      <slot v-else-if="!chargement" />
    </div>
  </section>
</template>

<script>
import { Icon } from '@iconify/vue';

/** Les teintes déclarées en fin d'`AccueilWidgets.vue`. Une teinte inconnue retombe sur l'indigo. */
const TEINTES = ['indigo', 'corail', 'emeraude', 'ambre', 'violet', 'ciel', 'rose'];

/**
 * Le cadre d'un widget.
 *
 * Chaque widget a sa teinte, comme les cordons d'une baie de brassage : on
 * retrouve un encart à sa couleur avant d'en lire le titre. La teinte colore le
 * fond de l'encart, la pastille d'icône et les chiffres — jamais le texte
 * courant, qui reste dans les couleurs du thème pour rester lisible.
 *
 * L'état se lit à part, par un anneau autour de l'encart : ambre quand quelque
 * chose attend, vert quand c'est sain. Une teinte est une identité, un anneau
 * est une alerte, et les deux ne se confondent pas.
 *
 * La variante `bandeau` sert le widget d'accueil pleine largeur : fond bleu
 * nuit, titre masqué (la salutation en tient lieu), mêmes commandes de
 * réglage — la poignée reste `.widget__poignee`, seule prise que la grille
 * accepte pour déplacer.
 */
export default {
  name: 'WidgetCadre',
  components: { Icon },
  props: {
    titre: { type: String, required: true },
    lien: { type: String, default: null },
    libelleLien: { type: String, default: 'Ouvrir' },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
    /** `attention` | `sain` | `repos`. Pilote l'anneau, rien d'autre. */
    etat: { type: String, default: 'repos' },
    teinte: { type: String, default: 'indigo' },
    icone: { type: String, default: null },
    /** `standard` | `bandeau`. */
    variante: { type: String, default: 'standard' },
  },
  emits: ['retirer', 'recharger'],
  computed: {
    teinteStyle() {
      const teinte = TEINTES.includes(this.teinte) ? this.teinte : 'indigo';

      return {
        '--accent': `var(--teinte-${teinte})`,
        '--encre': `var(--teinte-${teinte}-encre)`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.widget {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--accueil-carte);
  border: 1px solid var(--accueil-bord);
  border-radius: 14px;
  box-shadow: var(--accueil-ombre);
  transition: box-shadow 0.16s ease, border-color 0.16s ease;

  &:hover {
    border-color: var(--accueil-bord-survol);
    box-shadow: var(--accueil-ombre-survol);
  }

  /* L'état se lit au point posé près du titre, et à lui seul. L'ancien anneau
     plein faisait un mur orange dès que trois widgets attendaient quelque
     chose, et le signal se perdait dans sa propre insistance ; une bordure
     teintée refaisait la même chose en plus discret, tout en doublant un
     repère déjà donné. La carte reste donc la même pour les dix. */

  &__entete {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 14px 16px 2px;
  }

  /* Le glyphe porte la teinte, sur un fond de la même teinte très dilué : la
     couleur identifie le widget sans peindre la carte entière. */
  &__icone {
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: color-mix(in srgb, var(--accent) 13%, transparent);
    color: var(--accent);
    flex-shrink: 0;
  }

  &__titre {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.005em;
    color: var(--el-text-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__etat {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accueil-sain);
    flex-shrink: 0;
  }

  &--attention &__etat { background: var(--accueil-attention); }

  &__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: auto;
    flex-shrink: 0;
  }

  /* Le lien ne se peint plus : il attend le survol pour prendre la teinte. Dix
     pastilles colorées en haut à droite des cartes tiraient l'œil avant les
     chiffres, qui sont le contenu. */
  &__lien {
    padding: 3px 8px;
    border: none;
    border-radius: 7px;
    background: transparent;
    font-size: 12px;
    font-weight: 550;
    color: var(--el-text-color-secondary);
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;

    &:hover {
      background: color-mix(in srgb, var(--accent) 10%, transparent);
      color: var(--accent);
    }

    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 1px;
    }
  }

  &__poignee,
  &__retirer {
    display: flex;
    align-items: center;
    padding: 3px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--el-text-color-placeholder);
    /* Révélées au survol : voir le commentaire du gabarit. */
    opacity: 0;
    transition: opacity 0.12s ease;
  }

  &__poignee { cursor: move; }

  &__retirer {
    cursor: pointer;

    &:hover { color: var(--el-color-danger); }
  }

  &:hover &__poignee,
  &:hover &__retirer,
  &:focus-within &__poignee,
  &:focus-within &__retirer {
    opacity: 1;
  }

  &__corps {
    flex: 1;
    min-height: 0;
    padding: 10px 16px 16px;
    overflow: auto;
  }

  &__panne {
    margin: 2px 0;
    font-size: 12.5px;
    color: var(--accueil-attention);
  }

  &__relancer {
    margin-left: 4px;
    padding: 0;
    border: none;
    background: transparent;
    font-size: 12.5px;
    color: var(--accent);
    text-decoration: underline;
    cursor: pointer;
  }

  /* Le bandeau : il porte l'identité de la maison, pas celle d'un écran. */
  &--bandeau {
    position: relative;
    overflow: hidden;
    border-color: transparent;
    background: #02264a;
    color: #fff;
  }

  &--bandeau &__entete {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    padding: 10px 12px;
  }

  &--bandeau &__titre {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  &--bandeau &__poignee,
  &--bandeau &__retirer {
    color: rgba(255, 255, 255, 0.75);
  }

  &--bandeau &__corps {
    padding: 0;
    overflow: hidden;
  }
}

/* Les chiffres sont le contenu : ils prennent l'encre du thème, pas celle de la
   teinte. Un nombre coloré se lit moins bien qu'un nombre noir, et dix couleurs
   de chiffres sur un écran n'en hiérarchisent aucun. */
:deep(.chiffre) {
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  color: var(--el-text-color-primary);
}

:deep(.chiffre--grand) {
  display: block;
  font-size: 30px;
  font-weight: 640;
  line-height: 1.1;
}

:deep(.chiffre--moyen) {
  font-size: 17px;
  font-weight: 620;
  line-height: 1.2;
}

:deep(.etiquette) {
  font-size: 12px;
  line-height: 1.35;
  color: var(--el-text-color-secondary);
}

/* Ce qui se clique dans un widget — une ligne, une tuile — ne porte plus
   d'aplat : il se révèle au survol. Une liste de cinq blocs gris sur une carte
   blanche fait un escalier ; cinq lignes séparées d'un filet se lisent. */
:deep(.surface) {
  background: transparent;
  border-radius: 9px;
  transition: background-color 0.12s ease;
}

:deep(button.surface:hover),
:deep(a.surface:hover),
:deep(.surface--cliquable:hover) {
  background: var(--el-fill-color-light);
}

@media (prefers-reduced-motion: reduce) {
  .widget,
  .widget__poignee,
  .widget__retirer,
  .widget__lien,
  :deep(.surface) {
    transition: none;
  }
}
</style>
